import cx from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Flex,
  Search,
  Loader,
  Text,
  Pill,
  Button,
  PillWrapper,
  Accordion,
  Divider
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { DropArea } from "@/appComponents/Blocks/Composites/DragAndDrop/DropArea";
import { EmployeeDetail } from "./EmployeeDetail";
import { EmployeeFilterPopover } from "@/appComponents/Navigation/RightSidebar/Planning/EmployeeList/EmployeeFilterPopover";
import { enumRulePairLogicCondition } from "@coreapp/CoreApp/QRulePair";
import { FormatValueFromFieldName } from "@/Utils/DataLib";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { OverrideFactory } from "@coreapp/CoreApp/OverrideFactory";
import { SearchHandleRef } from "@theorgchartdev/storybook/dist/components/Search/Search";
import { searchTalent } from "../TalentPool/TalentPool.utils";
import type { QBoxDraggerPanel } from "@coreapp/CoreApp/QBoxDraggerPanel";
import type { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { usePlanOverrides } from "./usePlanOverrides";
import { useTopic } from "@/hooks/useTopic";
import BudgetDetailsDrawer from "@/appComponents/Blocks/Modals/RecordEditorModal/Components/BudgetDetailsDrawer";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { useRecordEditorController } from "@/appComponents/Blocks/Modals/RecordEditorModal/useRecordEditorController";
import { enumObjectTypes, enumOverrideType } from "@coreapp/constants";
import { PositionText } from "@/appComponents/Blocks/Modals/RecordEditorModal/Components/PositionText";
import { useTopicBlocker } from "@/hooks/useTopicBlocker";
import { ConflictAssignmentModal } from "./ConflictAssignmentModal";
import { useEmployeeAssignment } from "@/hooks/useEmployeeAssignment";
import { appstrp } from "@coreapp/utils/appstrp";
import { useCustomChartController } from "@/providers/useCustomChartController";
import {
  BaseChartEmployeeList,
  CustomChartEmployeeList,
  EntityEmployeeList
} from "./EmployeeList.utils";
import { List as VirtualList, useListRef } from "react-window";
import { useCursorPaginatedSearch } from "@/hooks/useCursorPaginatedSearch";
import {
  RSBFooter,
  RSBPrimaryButton,
  RSBSecondaryButton
} from "@/appComponents/Navigation/RightSidebar/Utils/RSBFooter";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

const NOOP = () => {};

const DEFAULT_VISIBLE_FILTERS = 5;
const ROW_HEIGHT = 28;

interface SearchOptions {
  sort?: {
    fieldName: string;
    order: "asc" | "desc";
  };
  cursor?: any;
  includeDeletes?: boolean;
  filters?: any;
  project?: string[];
  positionFilters?: any;
  employeeFilters?: any;
}

interface EmployeeListProps {
  initialSearchQuery?: string;
}

// Helper to create a composite field identifier
const createCompositeFieldId = (fieldId: string, className: string) => {
  return `${className}::${fieldId}`;
};

// Helper to extract field names and values from QFilterBlock for displaying pills
const extractFilterInfo = (
  filterBlock: QFilterBlock | null
): Array<{ field: string; className: string; value: string }> => {
  const filterInfo: Array<{ field: string; className: string; value: string }> = [];
  if (!filterBlock) return filterInfo;

  // Get field values map from filter block
  const rules = filterBlock.GetRules();
  if (!rules || !rules.length) return filterInfo;

  // Process each rule
  rules.forEach(rule => {
    const field = rule.GetFieldID();
    const className = rule.GetFieldClassName() || "Box"; // Default to Box if not specified
    const value = rule.GetValue();

    // Only skip null/undefined values
    if (!field || value === null || value === undefined) return;

    // Handle array values from "inlist" comparison
    if (Array.isArray(value)) {
      // Add each value individually
      value.forEach(v => {
        const stringValue = String(v);
        filterInfo.push({
          field,
          className,
          value: stringValue
        });
      });
    } else {
      // Handle single value - including empty strings
      const stringValue = String(value);
      filterInfo.push({
        field,
        className,
        value: stringValue
      });
    }
  });

  return filterInfo;
};

/** Wrapper that captures per-item data so EmployeeDetail receives stable props */
const EmployeeDetailRow = React.memo(function EmployeeDetailRow({
  employeeId,
  employee,
  refObj,
  checked,
  showCheckbox,
  onToggle
}: {
  employeeId: string;
  employee?: Record<string, unknown>;
  refObj?: QObjDBObject;
  checked: boolean;
  showCheckbox?: boolean;
  onToggle: (id: string, data: Record<string, unknown> | QObjDBObject) => void;
}) {
  const handleSelect = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent | React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      onToggle(employeeId, (refObj ?? employee) as Record<string, unknown> | QObjDBObject);
    },
    [employeeId, refObj, employee, onToggle]
  );

  return (
    <EmployeeDetail
      onSelect={showCheckbox ? handleSelect : NOOP}
      checked={checked}
      employee={employee}
      refObj={refObj}
      showCheckbox={showCheckbox}
    />
  );
});

interface VirtualRowData {
  results: Record<string, unknown>[];
  selectedEmployeeSet: Set<string>;
  showAssignmentTab: boolean;
  handleEmployeeToggle: (id: string, data: Record<string, unknown> | QObjDBObject) => void;
  getUniqueRecordId: (record: Record<string, unknown>, index: number) => string | number;
}

function VirtualRow({
  index,
  style,
  ariaAttributes,
  results,
  selectedEmployeeSet,
  showAssignmentTab,
  handleEmployeeToggle,
  getUniqueRecordId
}: {
  ariaAttributes: { "aria-posinset": number; "aria-setsize": number; role: "listitem" };
  index: number;
  style: React.CSSProperties;
} & VirtualRowData): React.ReactElement {
  const result = results[index];
  const employeeId = `${getUniqueRecordId(result, index)}`;

  return (
    <div style={style} {...ariaAttributes}>
      <EmployeeDetailRow
        employeeId={employeeId}
        employee={result}
        checked={showAssignmentTab ? selectedEmployeeSet.has(employeeId) : false}
        showCheckbox={showAssignmentTab}
        onToggle={handleEmployeeToggle}
      />
    </div>
  );
}

export const EmployeeList = (props: EmployeeListProps) => {
  const wsMgr = GetWSMgr();
  const doc = GetDoc();
  const isPlan = doc?.IsPlanChart();
  const isCustomChart = doc?.IsCustomChart();
  const planManager = wsMgr?.GetPlanMgr();
  const initialSearchQuery = props.initialSearchQuery ?? "";

  // Get assignment context from PlanManagerProvider
  const planningManagerContext = usePlanManagerContext();
  const customChartController = useCustomChartController();
  const { assignmentContextValue } = isCustomChart ? customChartController : planningManagerContext;
  const {
    showAssignmentTab,
    theData,
    onSaveRecordEdit,
    onClosePositionAssignment,
    setShowAssignmentTab
  } = assignmentContextValue || {};

  // Get the position object from theData
  const positionData = theData[enumObjectTypes.position];
  const dbObj = positionData?.dataObj;
  const overrideObj = positionData?.override;

  // Initialize the record editor controller to get save functionality
  // IMPORTANT: Always call hooks unconditionally to avoid "Rules of Hooks" violations
  const recordEditorController = useRecordEditorController({
    dbObj: dbObj || null,
    overrideObj: overrideObj || null,
    type: positionData?.type || enumOverrideType.change,
    manager: planManager,
    initChanges: positionData?.initChanges || {},
    isPlanEdit: true,
    branchAdd: positionData?.branchAdd || false,
    allowOrphans: false
  });

  const { SaveRecordChanges, processAssignmentChanges, GetHolder } = recordEditorController || {};

  const planOverrides = usePlanOverrides();
  const [queryString, setQueryString] = useState<string>(initialSearchQuery);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [sort, _setSort] = useState<{ fieldName: string; order: "asc" | "desc" }>(() => ({
    fieldName: doc?.GetBoxClass()?.GetFieldDefByID("Name")?.GetFieldName?.(),
    order: "asc"
  }));
  const {
    results,
    loading,
    handleFetch: fetchPage,
    handleNextPage
  } = useCursorPaginatedSearch<Record<string, unknown>>({
    searchFn: async (query, { sort: sortOpt, cursor }) => {
      if (!doc) return [];
      if (!isPlan) {
        return employeeListHandler.searchHolders(
          query,
          hasActiveFilters ? activeFilterBlock : null,
          sortOpt,
          cursor as string | undefined
        );
      }
      const andCondition: any = enumRulePairLogicCondition.and;
      const filterResult: any = activeFilterBlock
        ? activeFilterBlock.MarshallSeparated(andCondition, true)
        : {};
      const searchOptions: SearchOptions = {
        sort: sortOpt,
        ...(cursor !== undefined && { cursor }),
        includeDeletes: true,
        positionFilters: filterResult?.positionFilters || [],
        employeeFilters: filterResult?.employeeFilters || []
      };
      const response: Record<string, unknown>[] = await searchTalent(
        planManager,
        query,
        searchOptions
      );
      return response ?? [];
    },
    sort,
    initialLoading: true
  });

  const [assignedEmployees, setAssignedEmployees] = useState<QObjDBObject[]>([]);
  const [originalAssignedEmployees, setOriginalAssignedEmployees] = useState<QObjDBObject[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [conflicts, setConflicts] = useState<QObjDBObject[]>([]);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);
  const listRef = useListRef(null);

  // For now, only Custom and Base Charts
  const employeeListHandler: EntityEmployeeList = isCustomChart
    ? new CustomChartEmployeeList()
    : new BaseChartEmployeeList();

  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;

  // State for active filter block
  const [activeFilterBlock, setActiveFilterBlock] = useState<QFilterBlock | null>(null);
  // Track if filters are applied
  const hasActiveFilters =
    activeFilterBlock &&
    activeFilterBlock.GetRules().some(rule => {
      const value = rule.GetValue();
      // Treat empty strings as valid values (not falsy)
      return value !== null && value !== undefined;
    });
  const [showAllFilters, setShowAllFilters] = useState(false);

  const { handleEmployeeToggle: handleEmployeeToggleHook, checkEmployeeConflicts } =
    useEmployeeAssignment({
      assignedEmployees,
      setAssignedEmployees,
      selectedEmployees,
      setSelectedEmployees,
      currentPosition: dbObj,
      overrides: planOverrides,
      GetHolder,
      onConflict: conflictEmployees => {
        setConflicts(conflictEmployees);
      },
      immediateConflictDetection: false
    });

  const blockerAlertProps = {
    title: "Do you want to save assignment changes?",
    message: appstr("saveconfirm3"),
    confirmText: appstr("Save Changes"),
    denyText: appstr("Discard Changes"),
    onConfirm: async () => {
      await handleAssignmentChange();
    }
  };

  // Update dirty state whenever assignments change
  useEffect(() => {
    if (!showAssignmentTab) {
      setIsDirty(false);
      dirtyRef.current = false;
      return;
    }

    // Compare current assigned employees with original
    const currentIds = assignedEmployees
      .map(emp => emp.SmartGetFieldValue("EmployeeID") || emp.SmartGetFieldValue("RecordID"))
      .sort();

    const originalIds = originalAssignedEmployees
      .map(emp => emp.SmartGetFieldValue("EmployeeID") || emp.SmartGetFieldValue("RecordID"))
      .sort();

    const dirty = JSON.stringify(currentIds) !== JSON.stringify(originalIds);
    setIsDirty(dirty);
    dirtyRef.current = dirty;
  }, [assignedEmployees, originalAssignedEmployees, showAssignmentTab]);

  const shouldBlock = () => {
    return {
      doBlock: dirtyRef.current,
      alertParams: blockerAlertProps
    };
  };

  useTopicBlocker("rightSidebarChangeMode", shouldBlock);

  useEffect(() => {
    if (!showAssignmentTab || !dbObj) {
      return;
    }
    const holders = dbObj.GetHolderList() || [];
    setAssignedEmployees(holders);
    setOriginalAssignedEmployees([...holders]); // Store original state for dirty checking

    const holderIds = holders.map((holder: { SmartGetFieldValue: (arg0: string) => any }) =>
      String(holder.SmartGetFieldValue("EmployeeID") || holder.SmartGetFieldValue("RecordID"))
    );
    setSelectedEmployees(holderIds);
  }, [showAssignmentTab, dbObj]);

  const handleAssignmentChange = async () => {
    if (!SaveRecordChanges || !processAssignmentChanges || !onSaveRecordEdit) {
      console.error("Missing required functions for saving assignments");
      return;
    }

    const assignmentChanges = processAssignmentChanges(assignedEmployees);
    const conflictAssignments = assignmentChanges.added.filter((emp: QObjDBObject) =>
      checkEmployeeConflicts(emp)
    );

    if (conflictAssignments.length > 0) {
      setConflicts(conflictAssignments);
      return;
    }

    return await handleSaveAssignments(assignedEmployees);
  };

  const handleConflictModalConfirm = async () => {
    setConflicts([]);
    await handleSaveAssignments(assignedEmployees);
  };

  const handleConflictModalCancel = () => {
    setConflicts([]);
  };

  const handleSaveAssignments = async (assignees: any) => {
    const assignmentChanges = processAssignmentChanges(assignees);
    let result;

    if (isPlan) {
      result = await SaveRecordChanges(onSaveRecordEdit, { assignmentChanges }, false, false, {});
    } else {
      await employeeListHandler.handleAssignment(
        assignmentChanges.added,
        assignmentChanges.removed,
        dbObj
      );

      result = { success: true };
    }

    if (result?.success) {
      setOriginalAssignedEmployees([...assignees]);
      setIsDirty(false);
    }
    return result;
  };

  const handleFetch = (query: string) => {
    if (!doc) return;
    listRef.current?.scrollToRow({ index: 0 });
    fetchPage(query);
  };

  useEffect(() => {
    handleFetch(queryString);
  }, [planOverrides, planManager, activeFilterBlock]);

  // If the user clicks on the Recent Search option and the Employee List is already open
  const searchRef = useRef<SearchHandleRef>(null);
  const accordionHandleRef = useRef<any>(null);

  useTopic("OpenEmployeeList", (state: { employeeName: string }) => {
    if (searchRef.current) searchRef.current.setValue(state.employeeName);
  });

  useTopic("CustomChartChangesLoaded", () => {
    handleFetch(searchRef.current?.value || "");
  });

  if (isPlan && !planManager) {
    return null;
  }

  const handleEmployeeToggle = useCallback(
    (employeeId: string, employeeData: Record<string, unknown> | QObjDBObject) => {
      handleEmployeeToggleHook(employeeId, employeeData);
    },
    [handleEmployeeToggleHook]
  );

  const handleInput = (nextQuery: string) => {
    setQueryString(nextQuery);
    handleFetch(nextQuery);
  };

  const handleDragEnd = async (dropPanel: QBoxDraggerPanel) => {
    const dataObj = dropPanel.GetDBObjClone();
    let holders: QObjDBObject[];
    if (dataObj?.IsPositionObj()) {
      holders = dataObj.GetHolderList() ?? [];
    } else {
      holders = [dataObj];
    }

    if (isPlan) {
      await OverrideFactory.UnassignHolders(holders);
    } else {
      await employeeListHandler.handleAssignment([], holders, null);
    }
  };

  const getUniqueRecordId = useCallback(
    (record: Record<string, unknown>, index: number) => {
      const boxClass = doc?.GetBoxClass();
      const idFieldDef = boxClass?.GetRecordIDFieldDef();
      const fieldName = idFieldDef?.GetFieldName();
      if (!fieldName || !(fieldName in record) || !record[fieldName]) {
        return index;
      }

      return `${record[fieldName]}`;
    },
    [doc]
  );

  const handleRowsRendered = useCallback(
    (visibleRows: { startIndex: number; stopIndex: number }) => {
      if (isCustomChart) return;
      if (results.length >= 10 && visibleRows.stopIndex >= results.length - 10) {
        handleNextPage();
      }
    },
    [isCustomChart, handleNextPage, results.length]
  );

  const selectedEmployeeSet = useMemo(() => new Set(selectedEmployees), [selectedEmployees]);

  const rowData = useMemo<VirtualRowData>(
    () => ({
      results,
      selectedEmployeeSet,
      showAssignmentTab: !!showAssignmentTab,
      handleEmployeeToggle,
      getUniqueRecordId
    }),
    [results, selectedEmployeeSet, showAssignmentTab, handleEmployeeToggle, getUniqueRecordId]
  );

  const handleApplyFilterBlock = (filterBlock: QFilterBlock) => {
    setActiveFilterBlock(filterBlock);
  };

  // Clear all filters
  /* TODO add later
  const handleClearFilters = () => {
    setActiveFilterBlock(null);
    setShowAllFilters(false);
  }
   */
  // Remove a specific field-value combination from the filter
  const handleRemoveValue = (
    fieldInfo: { field: string; className: string; fieldDef?: any } | string | any,
    valueToRemove: string
  ): void => {
    if (!activeFilterBlock) return;

    // Handle both legacy and new field formats
    let fieldId, className;

    if (typeof fieldInfo === "object" && fieldInfo.fieldDef) {
      // Using fieldData with field definition - extract ID and class
      fieldId = fieldInfo.fieldDef.GetFieldID ? fieldInfo.fieldDef.GetFieldID() : fieldInfo.field;
      className = fieldInfo.fieldDef.GetFieldClassName
        ? fieldInfo.fieldDef.GetFieldClassName()
        : fieldInfo.className || "Box";
    } else if (typeof fieldInfo === "object" && fieldInfo.field && fieldInfo.className) {
      // Using simple object with field and className
      // Assume field is ID for backward compatibility
      fieldId = fieldInfo.field;
      className = fieldInfo.className;
    } else if (typeof fieldInfo === "string" && fieldInfo.includes("::")) {
      // Using composite ID
      const [classStr, idStr] = fieldInfo.split("::");
      className = classStr;
      fieldId = idStr;
    } else {
      // Using old format or unknown format, assume Box class
      // This is risky but preserves backward compatibility
      fieldId = typeof fieldInfo === "string" ? fieldInfo : fieldInfo.field || "";
      className = "Box";
    }

    // Clone the filter block
    const updatedFilterBlock = activeFilterBlock.Clone();

    // Get all rules
    const rules = updatedFilterBlock.GetRules();

    // Find the rule for this field and class
    const ruleToUpdate = rules.find(
      rule => rule.GetFieldID() === fieldId && rule.GetFieldClassName() === className
    );

    if (ruleToUpdate) {
      // Get current values (assuming they're stored as an array or can be converted to one)
      const currentValues = ruleToUpdate.GetValue();

      // Remove the specific value
      const updatedValues = Array.isArray(currentValues)
        ? currentValues.filter(v => v !== valueToRemove)
        : currentValues === valueToRemove
          ? []
          : currentValues === null || currentValues === undefined
            ? []
            : [currentValues];

      if (updatedValues.length === 0) {
        // If no values left, remove the entire rule
        ruleToUpdate.UpdateValue(null);
      } else if (updatedValues.length === 1) {
        // If only one value left, update to use equals operator
        ruleToUpdate.UpdateComparison("equals");
        ruleToUpdate.UpdateValue(updatedValues[0]);
      } else {
        // Otherwise update the rule with the filtered values and ensure inlist operator
        ruleToUpdate.UpdateComparison("inlist");
        ruleToUpdate.UpdateValue(updatedValues);
      }

      // Update the filter block
      setActiveFilterBlock(updatedFilterBlock);
    }
  };

  const formatPillLabel = (field: string, value: string) => {
    // Format the value for display
    const formattedValue = FormatValueFromFieldName(field, value);
    if (value === "") {
      return appstr("Blank");
    }
    return formattedValue;
  };

  // Extract filter info for display using helper function
  const filterInfo = extractFilterInfo(activeFilterBlock);

  return (
    <>
      <DropArea className="oc-fill-height" onDragEnd={handleDragEnd}>
        <Loader.Root>
          <Flex className="oc-staging-area--wrapper" direction="column" style={{ height: "100%" }}>
            <div className={cx("sidebar-header-buttons", "sidebar-content-item--divider")}>
              <Flex direction="column" gap="6px" width="100%">
                <Flex direction="row" alignItems="center" gap="8px">
                  <Search
                    size="medium"
                    placeholder={appstr("Search name")}
                    onInput={handleInput}
                    handleRef={searchRef}
                    initialValue={initialSearchQuery}
                    dataTestId={chartModeTestId("sidebar", "employee-list-search")}
                  />
                  <EmployeeFilterPopover
                    onApplyFilters={handleApplyFilterBlock}
                    currentFilterBlock={activeFilterBlock}
                  />
                </Flex>
                {results.length > 0 && (
                  <Text colorModifier="grey" size="small">
                    {appstr("Showing").toLowerCase()}: {`${results.length}`}{" "}
                    {appstr("Employees").toLowerCase()}
                    {loading && ` (${appstrp("Loading", "...").toLowerCase()})`}
                  </Text>
                )}
                {hasActiveFilters && (
                  <Flex direction="row" gap="8px" wrap="wrap" margin="4px 0">
                    {/* Show either the default number or all filters based on state */}
                    {filterInfo
                      .slice(0, showAllFilters ? filterInfo.length : DEFAULT_VISIBLE_FILTERS)
                      .map(({ field, className, value }) => {
                        // Create composite ID for key
                        const compositeId = createCompositeFieldId(field, className);
                        return (
                          <PillWrapper
                            key={`filter-${compositeId}-${value}`}
                            style={{
                              display: "inline-block",
                              position: "relative",
                              margin: "2px"
                            }}
                          >
                            <Pill
                              className={"oc-filter-pill"}
                              onRemove={e => {
                                e.stopPropagation();
                                handleRemoveValue({ field, className }, value);
                              }}
                              label={formatPillLabel(field, value)}
                            />
                          </PillWrapper>
                        );
                      })}

                    {/* Toggle button - show more or show less */}
                    {filterInfo.length > DEFAULT_VISIBLE_FILTERS && (
                      <Button
                        size="small"
                        modifier="underline"
                        className={
                          showAllFilters ? "show-less-filters-link" : "show-more-filters-link"
                        }
                        onClick={() => setShowAllFilters(!showAllFilters)}
                        dataTestId={chartModeTestId("sidebar", "employee-list-toggle-filters-btn")}
                      >
                        {showAllFilters
                          ? appstr("Show less")
                          : appstr("Show all") +
                            ` (${filterInfo.length - DEFAULT_VISIBLE_FILTERS}+)`}
                      </Button>
                    )}
                  </Flex>
                )}
              </Flex>
            </div>

            {/* Content Area - Employee List */}
            <Flex
              className={cx("sidebar-content-container")}
              direction="column"
              flex={showAssignmentTab ? (isAccordionExpanded ? "1 1 20%" : "1 1 auto") : "1 1 auto"}
              gap="4px"
              height="100%"
              style={{
                minHeight: showAssignmentTab ? "200px" : "auto",
                overflow: "hidden",
                ...(dbObj
                  ? {
                      paddingBottom: "0"
                    }
                  : {})
              }}
            >
              {showAssignmentTab && selectedEmployees.length > 0 && (
                <Flex direction="column" gap="8px" margin="0 0 4px 0" className="oc-shrink-0">
                  <Flex direction="column" gap="8px">
                    <Text size="medium" modifier="bold">
                      {appstr("Selections")} ({selectedEmployees.length})
                    </Text>
                    <Flex direction="column" gap="4px">
                      {assignedEmployees.map((employee, index) => {
                        const employeeId = String(
                          employee.SmartGetFieldValue("EmployeeID") ||
                            employee.SmartGetFieldValue("RecordID")
                        );

                        return (
                          <EmployeeDetailRow
                            key={`selected-${employeeId}-${index}`}
                            employeeId={employeeId}
                            refObj={employee}
                            checked={selectedEmployees.includes(employeeId)}
                            showCheckbox={showAssignmentTab}
                            onToggle={handleEmployeeToggle}
                          />
                        );
                      })}
                    </Flex>
                  </Flex>
                  <Divider />
                </Flex>
              )}

              {results.length === 0 && !loading ? (
                <Flex justifyContent="center" alignItems="center">
                  <Text colorModifier="grey" size="small">
                    {appstr("nomatches")}
                  </Text>
                </Flex>
              ) : results.length > 0 ? (
                <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
                  <VirtualList<VirtualRowData>
                    listRef={listRef}
                    rowCount={results.length}
                    rowHeight={ROW_HEIGHT}
                    rowComponent={VirtualRow}
                    rowProps={rowData}
                    onRowsRendered={handleRowsRendered}
                    className="ocn-list-scrollbar"
                    overscanCount={5}
                    defaultHeight={400}
                  />
                </div>
              ) : null}
            </Flex>
            {/* Position Details Section */}
            {showAssignmentTab && dbObj && (
              <Flex
                direction="column"
                flex={isAccordionExpanded ? (isCustomChart ? "none" : "1 1 50%") : "0 0 auto"}
                style={{
                  minHeight: isAccordionExpanded && !isCustomChart ? "200px" : "auto",
                  borderTop: "1px solid #e0e0e0"
                }}
              >
                {/* Scrollable Content Area */}
                {!isCustomChart && (
                  <Flex
                    flex={isAccordionExpanded ? "1" : "0 0 auto"}
                    direction={"column"}
                    overflow={"hidden"}
                  >
                    <Accordion
                      name={
                        <Text size={"medium"} modifier={"bold"}>
                          {"Position details"}
                        </Text>
                      }
                      initExpand={isAccordionExpanded}
                      onToggle={setIsAccordionExpanded}
                      iconPosition="right"
                      labelProps={{
                        backgroundColor: "orange",
                        hoverColor: "orange",
                        customStyle: { justifyContent: "space-between", padding: "12px" }
                      }}
                      beforeClass={cx("oc-overflow-hidden", "oc-flex")}
                      containerClass={cx("oc-overflow-hidden")}
                      handleRef={accordionHandleRef}
                      beforeContent={
                        <div
                          className={cx("ocn-list-scrollbar")}
                          style={{
                            flex: "1",
                            overflowY: "auto",
                            overflowX: "hidden",
                            padding: "0 16px 16px 16px"
                          }}
                        >
                          <BudgetDetailsDrawer
                            isOpen={true}
                            position={dbObj}
                            assignedEmployees={assignedEmployees as never}
                            originalAssignedEmployees={originalAssignedEmployees as never}
                            dataTestIdPrefix={"employee-list-budget-drawer"}
                          />
                        </div>
                      }
                    />
                  </Flex>
                )}

                {/* Footer */}
                <RSBFooter
                  preButtonText={
                    <PositionText
                      positionObject={dbObj}
                      templateStr={"Position selected"}
                      positionTitle={""}
                      positionID={""}
                      textProps={{ size: "small" }}
                    />
                  }
                >
                  <RSBSecondaryButton
                    dataTestId={chartModeTestId("sidebar", "employee-list-assignment-cancel-btn")}
                    onClick={() => {
                      if (onClosePositionAssignment) {
                        onClosePositionAssignment();
                      } else {
                        setShowAssignmentTab?.(false);
                      }
                      doc?.GetCanvasRef()?.ClearSelection();
                    }}
                  />
                  <RSBPrimaryButton
                    disabled={!isDirty}
                    onClick={handleAssignmentChange}
                    iconProps={{ name: "user-plus" }}
                    dataTestId={chartModeTestId("sidebar", "employee-list-assign-btn")}
                  >
                    {appstr("Assign")}
                  </RSBPrimaryButton>
                </RSBFooter>
              </Flex>
            )}
          </Flex>
        </Loader.Root>
      </DropArea>

      {/* Conflict Assignment Modal */}
      {conflicts.length > 0 && dbObj && (
        <ConflictAssignmentModal
          show={conflicts.length > 0}
          conflicts={conflicts}
          targetPosition={dbObj}
          onConfirm={handleConflictModalConfirm}
          onCancel={handleConflictModalCancel}
        />
      )}
    </>
  );
};

/* TODO add later
{filterInfo.length > 0 && (
  <Button size="small" modifier="secondary" onClick={handleClearFilters}>
    {appstr("Clear all")}
  </Button>
)}
 */
